/**
 * The Blackjack room's tabs.
 *
 * ============================== PHASE 8: ON THE KIT ============================
 * Phase 2b gave the room the same front door every other room has and changed nothing
 * about the table. Phase 8 re-based the table itself (`routes/Blackjack.tsx` on
 * `table/BlackjackTable.tsx`), and this file follows it:
 *
 *   - THE PLAY CARDS carry a card id, a fidelity and a title, so the seat card, the
 *     room header and the Ledger all name the table the player picked.
 *   - THE TABLE is its own full-screen route now, like poker's — the tab bar and the
 *     reading column were never going to leave a phone room for a half-moon and a band.
 *   - THE TRAIN CARDS ARE LIVE. Each opens `/blackjack/train/run?card=…`; the three
 *     drills are in `BlackjackDrills.tsx`. The Count Drill still says, on its own
 *     face, that it is an exercise and not an edge: this room reshuffles every hand.
 * ================================================================================
 */
import { useSearchParams } from "react-router-dom";
import { PresetCard } from "./PresetCard";
import { Blackjack } from "../routes/Blackjack";
import { BJ_TRAIN_CARDS } from "./bj-copy";
import type { Fidelity } from "@engine/blackjack/bots";
import { money } from "../lib/api";
import { UNLOCKS } from "@config/plans";
import { useUnlocks } from "../lib/useUnlocks";
import "./rooms.css";

/** The three Play cards: one opening position each of the table's own two controls. */
export const BJ_PLAY_CARDS = [
  { id: "quick", title: "Quick Table", seats: 1, fidelity: "casual" as Fidelity,
    line: "One seat, the dealer, and a verdict on every decision as you make it." },
  { id: "full", title: "Full Table", seats: 5, fidelity: "casual" as Fidelity,
    line: "Five seats. The other players are dealt in, and their cards change your odds." },
  { id: "custom", title: "Custom Table", seats: 3, fidelity: "casual" as Fidelity,
    line: "Three seats to start, and the table's own controls for who is at it." },
] as const;

const tableUrl = (c: (typeof BJ_PLAY_CARDS)[number]) =>
  `/blackjack/play/table?seats=${c.seats}&fidelity=${c.fidelity}&card=${c.id}`;

export function BlackjackPlay() {
  const [quick, full, custom] = BJ_PLAY_CARDS;
  return (
    <div className="tab-play">
      <div className="cardgrid">
        <PresetCard hero title={quick.title} action="Sit down" line={quick.line} to={tableUrl(quick)} />
        <PresetCard title={full.title} action="Sit down" line={full.line} to={tableUrl(full)} />
        <PresetCard quiet title={custom.title} action="Open" line={custom.line} to={tableUrl(custom)} />
      </div>
      <p className="tab-note muted small">
        Two decks, reshuffled every single hand. The tables are free on every plan, and
        every decision gets basic strategy&rsquo;s verdict. The exact numbers — worked out
        from the deck as it stands, rather than estimated — are Blackjack Pro,{" "}
        {money(UNLOCKS.blackjack_pro.amountCents)} once or included in Poker Pro.
      </p>
    </div>
  );
}

/**
 * The table, addressed by its URL. Keyed on the seats and the opponents because
 * changing either is a new table, and so a new sitting: the old one unmounts, which is
 * one of the three exits that settle it.
 */
export function BlackjackTable() {
  const [params] = useSearchParams();
  const n = Number(params.get("seats"));
  const seats = [1, 3, 5].includes(n) ? n : 3;
  const f = params.get("fidelity");
  const fidelity: Fidelity = f === "sharp" || f === "loose" ? f : "casual";
  const card = BJ_PLAY_CARDS.find((c) => c.id === params.get("card")) ?? null;
  const title = card?.title ?? "Custom Table";
  return <Blackjack key={`${seats}/${fidelity}`}
                    preset={{ seats, fidelity, title, cardId: card?.id ?? "custom" }} />;
}

/**
 * PHASE 9 (master plan §9): the Count Drill and Decision Spots are Blackjack Pro. Their
 * cards stay on the shelf for everybody — a player should see what the unlock buys —
 * and name it; the drill page itself says the rest.
 */
export function BlackjackTrain() {
  const { has, loading } = useUnlocks();
  const owned = has("blackjack_pro");
  return (
    <div className="tab-train">
      <div className="cardgrid">
        {BJ_TRAIN_CARDS.map((c, i) => (
          <PresetCard key={c.id} hero={i === 0} title={c.title} line={c.line} to={c.href}
                      action={c.pro && !owned && !loading ? "See it" : "Start"}
                      chip={c.pro && !owned && !loading ? "Blackjack Pro" : undefined} />
        ))}
      </div>
    </div>
  );
}
